import { SearchInput } from 'components/form/Input';
import CustomTable from 'components/layouts/CustomTable';
import Loader from 'components/layouts/Loader';
import React, { useEffect, useState } from 'react';
import useUserStore, { UserProps } from 'state/userStore';
import { AccountProps } from 'state/accountStore';
import { NumberDisplay } from 'components/layouts/Display';
import Button from 'components/form/Button';
import { toast } from 'react-toastify';
import { getAccounts } from 'api/account';
import AccountModal from 'pages/admin/modal/AccountModal';

export type AccountWithUser = AccountProps & { user: UserProps } & {
  sn: number;
};

const Accounts = () => {
  window.document.title = 'Accounts';

  const { user } = useUserStore();

  const [data, setData] = useState<AccountWithUser[]>([]);
  const [query, setQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<AccountWithUser | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchList, setSearchList] = useState<AccountWithUser[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = (searchStr: string) => {
    setQuery(searchStr);
    const studentList = data.filter(
      (item) =>
        item.user.fullName.toLowerCase().startsWith(searchStr) ||
        item.user.fullName.toLowerCase().endsWith(searchStr) ||
        item.user.email.toLowerCase().startsWith(searchStr) ||
        item.user.country.toLowerCase().startsWith(searchStr)
    );

    setSearchList(studentList);
  };

  const columns = [
    {
      Header: 'S/N',
      accessor: 'sn',
      Cell: ({ row: { original } }: any) => {
        const { sn } = original;
        return <p>{sn}</p>;
      },
    },
    {
      Header: 'Full Name',
      accessor: 'user.fullName',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const {
          user: { fullName },
        } = original;
        return <p>{fullName}</p>;
      },
    },
    {
      Header: 'Email',
      accessor: 'user.email',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const {
          user: { email },
        } = original;
        return <p>{email}</p>;
      },
    },
    {
      Header: 'Balance',
      accessor: 'balance',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { balance } = original;
        return <NumberDisplay displayType="text" value={+balance} prefix="$" />;
      },
    },
    {
      Header: 'Wallet Balance',
      accessor: 'walletBalance',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { walletBalance } = original;
        return (
          <NumberDisplay displayType="text" value={+walletBalance} prefix="$" />
        );
      },
    },
    {
      Header: 'Profit',
      accessor: 'profit',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { profit } = original;
        return <NumberDisplay displayType="text" value={+profit} prefix="$" />;
      },
    },
    {
      Header: 'Investment',
      accessor: 'investment',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { investment } = original;
        return (
          <NumberDisplay displayType="text" value={+investment} prefix="$" />
        );
      },
    },
    {
      Header: 'Bitcoin',
      accessor: 'bitcoin',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { bitcoin } = original;
        return <NumberDisplay displayType="text" value={+bitcoin} prefix="$" />;
      },
    },
    {
      Header: 'Litecoin',
      accessor: 'litecoin',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { litecoin } = original;
        return (
          <NumberDisplay displayType="text" value={+litecoin} prefix="$" />
        );
      },
    },
    {
      Header: 'BNB',
      accessor: 'bnb',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { bnb } = original;
        return <NumberDisplay displayType="text" value={+bnb} prefix="$" />;
      },
    },
    {
      Header: 'Ethereum',
      accessor: 'ethereum',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { ethereum } = original;
        return (
          <NumberDisplay displayType="text" value={+ethereum} prefix="$" />
        );
      },
    },
    {
      Header: 'USDT',
      accessor: 'usdt',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { usdt } = original;
        return <NumberDisplay displayType="text" value={+usdt} prefix="$" />;
      },
    },
    {
      Header: '',
      accessor: 'options',
      disableSortBy: true,
      Cell: ({ row: { original } }: any) => {
        return (
          <Button onClick={() => selectUser(original)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
              />
            </svg>
          </Button>
        );
      },
    },
  ];

  const fetchAccounts = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await getAccounts(user.type);

      const data: AccountWithUser[] = res.data;

      setData(data.map((item, index) => ({ ...item, sn: index + 1 })));
    } catch (error: any) {
      const { errors } = error.response.data;

      errors.forEach((err: { error: string }) => {
        toast.error(error);
      });

      console.log(error);
    }
    setLoading(false);
  };

  const selectUser = (data: AccountWithUser) => {
    setSelectedUser(data);
    setIsOpen(true);
  };

  const handleClose = () => {
    setSelectedUser(null);
    setIsOpen(false);
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  return (
    <div className="px-5">
      <div className="flex flex-col gap-y-4">
        <h3 className="text-xl uppercase">Accounts Table</h3>
        <SearchInput
          name="search"
          placeholder="search by user"
          handleChange={(value) => handleSearch(value)}
          value={query}
        />
      </div>
      <br />
      {loading ? (
        <Loader />
      ) : (
        <CustomTable
          data={searchList.length ? searchList : data}
          columnShape={columns}
          size={20}
        />
      )}

      {isOpen && !!selectedUser && (
        <AccountModal
          data={selectedUser}
          closeModal={handleClose}
          reload={fetchAccounts}
        />
      )}
    </div>
  );
};

export default Accounts;
